import {
  CheckBadgeIcon,
  EllipsisHorizontalIcon,
} from '@heroicons/react/20/solid';
import React from 'react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { TableCell, TableRow } from '@/components/ui/table';
import type { ReadPostgridContact } from '@/services/api/generated/postgridService';

interface ContactTableRowProps {
  contact: ReadPostgridContact;
  onView?: (contact: ReadPostgridContact) => void;
  onDelete?: (contact: ReadPostgridContact) => void;
  onRetrySync?: (contact: ReadPostgridContact) => void;
}

const ContactTableRow = ({
  contact,
  onView,
  onDelete,
  onRetrySync,
}: ContactTableRowProps) => {
  // Direct display name logic - no utility functions
  const getDisplayName = (): string => {
    const firstName = contact.first_name?.trim() || '';
    const lastName = contact.last_name?.trim() || '';
    const companyName = contact.company_name?.trim() || '';

    if (firstName || lastName) {
      const fullName = `${firstName} ${lastName}`.trim();
      return companyName ? `${fullName} (${companyName})` : fullName;
    }

    return companyName || 'Unnamed Contact';
  };

  // Direct address logic
  const getAddress = (): string | null => {
    const parts = [
      contact.address_line1,
      contact.city,
      contact.province_or_state,
      contact.postal_or_zip,
    ].filter(Boolean);

    return parts.length > 0 ? parts.join(', ') : null;
  };

  const displayName = getDisplayName();
  const address = getAddress();
  const canRetry = !contact.postgrid_id;
  const isTest = !contact.live;
  const canView = Boolean(onView);

  const handleRowClick = () => {
    if (canView) {
      onView?.(contact);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.key === 'Enter' || e.key === ' ') && canView) {
      e.preventDefault();
      onView?.(contact);
    }
  };

  return (
    <TableRow
      key={contact.id ?? `contact-${Math.random()}`}
      className={
        canView
          ? 'cursor-pointer hover:bg-gray-50/80 focus-within:bg-gray-50/80 transition-colors duration-150 border-b border-gray-100'
          : 'border-b border-gray-100'
      }
      onClick={handleRowClick}
      onKeyDown={handleKeyDown}
      tabIndex={canView ? 0 : -1}
      role={canView ? 'button' : undefined}
      aria-label={canView ? `View details for ${displayName}` : undefined}
    >
      {/* Contact */}
      <TableCell className="py-3 pl-4">
        <div className="min-w-0">
          <div className="text-sm font-medium text-gray-900">{displayName}</div>
          <div className="flex items-center gap-2 mt-1">
            {isTest && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                TEST
              </span>
            )}
            {contact.is_verified && (
              <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
                <CheckBadgeIcon className="h-3 w-3" />
                Verified
              </span>
            )}
          </div>
        </div>
      </TableCell>

      {/* Address */}
      <TableCell className="py-3">
        {address ? (
          <div className="text-sm text-gray-600">{address}</div>
        ) : (
          <span className="text-sm text-gray-400">No address</span>
        )}
      </TableCell>

      {/* Actions */}
      <TableCell className="py-3 pr-4">
        <div
          className="-mx-2 -my-1"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          role="none"
        >
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 text-gray-400 hover:text-gray-600"
                aria-label={`More options for ${displayName}`}
              >
                <EllipsisHorizontalIcon className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {onView && (
                <DropdownMenuItem onClick={() => onView(contact)}>
                  View Details
                </DropdownMenuItem>
              )}
              {canRetry && onRetrySync && (
                <DropdownMenuItem onClick={() => onRetrySync(contact)}>
                  Retry Sync
                </DropdownMenuItem>
              )}
              {onDelete && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => onDelete(contact)}
                    className="text-red-600 focus:text-red-600"
                  >
                    Delete
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </TableCell>
    </TableRow>
  );
};

export { ContactTableRow };
